import { useState, useEffect } from "react";
import axios from "axios";
import { PageLayout } from "../components/PageLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  Download,
  Users,
  Building,
  UserCheck,
  Search,
  MapPin,
  Phone,
  Mail,
  CreditCard,
} from "lucide-react";

const API = `${window.location.origin}/api`;

const REGIONI = [
  "Abruzzo", "Basilicata", "Calabria", "Campania", "Emilia-Romagna",
  "Friuli-Venezia Giulia", "Lazio", "Liguria", "Lombardia", "Marche",
  "Molise", "Piemonte", "Puglia", "Sardegna", "Sicilia", "Toscana",
  "Trentino-Alto Adige", "Umbria", "Valle d'Aosta", "Veneto"
];

const AdminReferentiPage = () => {
  const [referenti, setReferenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterRuolo, setFilterRuolo] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clientiDialogOpen, setClientiDialogOpen] = useState(false);
  const [selectedReferente, setSelectedReferente] = useState(null);
  const [clienti, setClienti] = useState([]);
  const [formData, setFormData] = useState({
    referente_id: "",
    ragione_sociale: "",
    nome: "",
    indirizzo: "",
    cap: "",
    citta: "",
    provincia: "",
    regione: "",
    telefono: "",
    email: "",
    ruolo: "AGENTE",
  });

  useEffect(() => {
    fetchReferenti();
  }, []);

  const fetchReferenti = async () => {
    try {
      const response = await axios.get(`${API}/referenti`);
      setReferenti(response.data);
    } catch (error) {
      toast.error("Errore nel caricamento dei referenti");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedReferente) {
        await axios.put(`${API}/admin/referenti/${selectedReferente.referente_id}`, formData);
        toast.success("Referente aggiornato");
      } else {
        await axios.post(`${API}/admin/referenti`, formData);
        toast.success("Referente creato");
      }
      setDialogOpen(false);
      resetForm();
      fetchReferenti();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante il salvataggio");
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/admin/referenti/${selectedReferente.referente_id}`);
      toast.success("Referente eliminato");
      setDeleteDialogOpen(false);
      fetchReferenti();
    } catch (error) {
      toast.error("Errore durante l'eliminazione");
    }
  };

  const handleViewClienti = async (referente) => {
    try {
      const response = await axios.get(`${API}/admin/referenti/${referente.referente_id}/clienti`);
      setClienti(response.data);
      setSelectedReferente(referente);
      setClientiDialogOpen(true);
    } catch (error) {
      toast.error("Errore nel caricamento dei clienti");
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API}/admin/referenti/export`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'referenti.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Export completato");
    } catch (error) {
      toast.error("Errore durante l'export");
    }
  };

  const openEditDialog = (referente) => {
    setSelectedReferente(referente);
    setFormData({
      referente_id: referente.referente_id,
      ragione_sociale: referente.ragione_sociale || "",
      nome: referente.nome,
      indirizzo: referente.indirizzo || "",
      cap: referente.cap || "",
      citta: referente.citta || "",
      provincia: referente.provincia,
      regione: referente.regione,
      telefono: referente.telefono || "",
      email: referente.email || "",
      ruolo: referente.ruolo,
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setSelectedReferente(null);
    setFormData({
      referente_id: "",
      ragione_sociale: "",
      nome: "",
      indirizzo: "",
      cap: "",
      citta: "",
      provincia: "",
      regione: "",
      telefono: "",
      email: "",
      ruolo: "AGENTE",
    });
  };

  const filteredReferenti = referenti.filter((r) => {
    const matchesSearch = 
      r.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.referente_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (r.ragione_sociale && r.ragione_sociale.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (r.citta && r.citta.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesRuolo = filterRuolo === "all" || r.ruolo === filterRuolo;
    return matchesSearch && matchesRuolo;
  });

  const agentiCount = referenti.filter(r => r.ruolo === "AGENTE").length;
  const distributoriCount = referenti.filter(r => r.ruolo === "DISTRIBUTORE").length;

  return (
    <PageLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl md:text-4xl font-semibold text-foreground mb-2">
              Distributori e Agenti
            </h1>
            <p className="text-muted-foreground">
              Gestisci la rete di distribuzione ODR
            </p>
          </div>
          <div className="flex gap-2 mt-4 md:mt-0">
            <Button variant="outline" className="rounded-full" onClick={handleExportCSV}>
              <Download className="w-4 h-4 mr-2" />
              Esporta CSV
            </Button>
            <Button className="rounded-full" onClick={() => { resetForm(); setDialogOpen(true); }}>
              <Plus className="w-4 h-4 mr-2" />
              Nuovo Referente
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card className="rounded-xl border-border/50">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <UserCheck className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{agentiCount}</p>
                <p className="text-sm text-muted-foreground">Agenti</p>
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-xl border-border/50">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                <Building className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{distributoriCount}</p>
                <p className="text-sm text-muted-foreground">Distributori</p>
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-xl border-border/50">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                <Users className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{referenti.reduce((acc, r) => acc + (r.clienti_count || 0), 0)}</p>
                <p className="text-sm text-muted-foreground">Clienti Totali</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="rounded-2xl border-border/50 mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Cerca per nome, ID, ragione sociale, città..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 rounded-xl"
                  />
                </div>
              </div>
              <Select value={filterRuolo} onValueChange={setFilterRuolo}>
                <SelectTrigger className="w-full md:w-48 rounded-xl">
                  <SelectValue placeholder="Filtra per ruolo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i ruoli</SelectItem>
                  <SelectItem value="AGENTE">Solo Agenti</SelectItem>
                  <SelectItem value="DISTRIBUTORE">Solo Distributori</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Referenti List */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-24 bg-secondary rounded-xl animate-pulse" />
            ))}
          </div>
        ) : filteredReferenti.length > 0 ? (
          <div className="space-y-3">
            {filteredReferenti.map((referente) => (
              <Card key={referente.referente_id} className="rounded-xl border-border/50">
                <CardContent className="p-4">
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="flex items-center gap-4 flex-1">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        referente.ruolo === "AGENTE" ? "bg-blue-100" : "bg-purple-100"
                      }`}>
                        {referente.ruolo === "AGENTE" ? (
                          <UserCheck className="w-6 h-6 text-blue-600" />
                        ) : (
                          <Building className="w-6 h-6 text-purple-600" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-medium">{referente.nome}</h4>
                          <Badge variant="outline" className="rounded-full font-mono text-xs">
                            {referente.referente_id}
                          </Badge>
                          <Badge className={`rounded-full ${
                            referente.ruolo === "AGENTE" ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700"
                          }`}>
                            {referente.ruolo}
                          </Badge>
                        </div>
                        {referente.ragione_sociale && (
                          <p className="text-sm text-muted-foreground">{referente.ragione_sociale}</p>
                        )}
                        <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground flex-wrap">
                          {referente.citta && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" /> {referente.citta} ({referente.provincia})
                            </span>
                          )}
                          {referente.email && (
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3" /> {referente.email}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-full"
                        onClick={() => handleViewClienti(referente)}
                      >
                        <Users className="w-4 h-4 mr-1" />
                        {referente.clienti_count || 0} clienti
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="rounded-full"
                        onClick={() => openEditDialog(referente)}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="rounded-full text-destructive hover:text-destructive"
                        onClick={() => { setSelectedReferente(referente); setDeleteDialogOpen(true); }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="rounded-2xl border-border/50">
            <CardContent className="py-16 text-center">
              <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
              <p className="text-muted-foreground">Nessun referente trovato</p>
            </CardContent>
          </Card>
        )}

        {/* Info Card */}
        <Card className="rounded-2xl border-border/50 mt-6 bg-primary/5">
          <CardContent className="p-6">
            <h3 className="font-medium mb-2 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-primary" />
              Formato ID Referente
            </h3>
            <p className="text-sm text-muted-foreground mb-2">
              Il codice ID segue il formato: <code className="bg-secondary px-2 py-1 rounded">ODR-[INIZIALE]-[PROVINCIA]</code>
            </p>
            <p className="text-sm text-muted-foreground">
              Esempio: <code className="bg-secondary px-2 py-1 rounded">ODR-B01-PI01</code> dove B è l'iniziale del cognome e PI la provincia.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-2xl max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">
              {selectedReferente ? "Modifica Referente" : "Nuovo Referente"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>ID Referente *</Label>
                <Input
                  value={formData.referente_id}
                  onChange={(e) => setFormData({ ...formData, referente_id: e.target.value.toUpperCase() })}
                  placeholder="ODR-B01-PI01"
                  required
                  disabled={!!selectedReferente}
                  className="rounded-xl uppercase"
                />
              </div>
              <div className="space-y-2">
                <Label>Ruolo *</Label>
                <Select value={formData.ruolo} onValueChange={(v) => setFormData({ ...formData, ruolo: v })}>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="AGENTE">Agente</SelectItem>
                    <SelectItem value="DISTRIBUTORE">Distributore</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nome/Riferimento *</Label>
                <Input
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  placeholder="Mario Rossi"
                  required
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label>Ragione Sociale</Label>
                <Input
                  value={formData.ragione_sociale}
                  onChange={(e) => setFormData({ ...formData, ragione_sociale: e.target.value })}
                  placeholder="Nome Azienda SRL"
                  className="rounded-xl"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Indirizzo</Label>
              <Input
                value={formData.indirizzo}
                onChange={(e) => setFormData({ ...formData, indirizzo: e.target.value })}
                placeholder="Via Roma 1"
                className="rounded-xl"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>CAP</Label>
                <Input
                  value={formData.cap}
                  onChange={(e) => setFormData({ ...formData, cap: e.target.value })}
                  placeholder="00100"
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label>Città</Label>
                <Input
                  value={formData.citta}
                  onChange={(e) => setFormData({ ...formData, citta: e.target.value })}
                  placeholder="Roma"
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label>Provincia *</Label>
                <Input
                  value={formData.provincia}
                  onChange={(e) => setFormData({ ...formData, provincia: e.target.value.toUpperCase() })}
                  placeholder="RM"
                  required
                  maxLength={2}
                  className="rounded-xl uppercase"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Regione *</Label>
              <Select value={formData.regione} onValueChange={(v) => setFormData({ ...formData, regione: v })}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Seleziona regione" />
                </SelectTrigger>
                <SelectContent>
                  {REGIONI.map((regione) => (
                    <SelectItem key={regione} value={regione}>{regione}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Telefono</Label>
                <Input
                  value={formData.telefono}
                  onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                  placeholder="+39 333 1234567"
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="email@esempio.it"
                  className="rounded-xl"
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" className="rounded-full" onClick={() => setDialogOpen(false)}>
                Annulla
              </Button>
              <Button type="submit" className="rounded-full">
                {selectedReferente ? "Salva Modifiche" : "Crea Referente"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Elimina Referente</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare {selectedReferente?.nome}? I clienti associati non saranno eliminati ma perderanno l'associazione.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Clienti Dialog */}
      <Dialog open={clientiDialogOpen} onOpenChange={setClientiDialogOpen}>
        <DialogContent className="rounded-2xl max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">
              Clienti di {selectedReferente?.nome}
            </DialogTitle>
          </DialogHeader>
          {clienti.length > 0 ? (
            <div className="space-y-3">
              {clienti.map((cliente) => (
                <div key={cliente.user_id} className="p-3 bg-secondary/50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Users className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">{cliente.name}</p>
                      <p className="text-sm text-muted-foreground">{cliente.email}</p>
                      <p className="text-xs text-muted-foreground">{cliente.centro_estetico}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">Nessun cliente associato</p>
          )}
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
};

export default AdminReferentiPage;
